/* Forest Lake (apps/luminous-lake): first-party arrival beacon.

   The AI-traffic project counts one thing as certain: a row the page itself wrote,
   with internal and automated both false, saying an assistant sent this browser. The
   nginx log behind it cannot know either. This game is static (pm2 serve of dist/) and
   has no server of its own, so the row goes to the campaign API, which is the only
   writer of apps/luminous-lake/data/events.jsonl.

   It is a game first. Nothing here touches the render loop, nothing here throws, and
   when the endpoint is unreachable the visit is simply not recorded.

   Sky Isles and the flight simulator carry the same file with their own APP and HOST.
   Keep the three in step; the row shape is read by scripts/ai_traffic_siblings.py
   beacon_sessions(), which names this store forest-lake after its host. */

const APP = 'forest-lake';
const HOST = 'forest-lake.bles-software.com';
const ENDPOINT = 'https://campaign-api.bles-software.com/v1/demo-beacon';
const VISITOR_KEY = 'bles_visitor';
const INTERNAL_KEY = 'bles_internal';
const TOUCH_KEY = 'bles_touch';
// A 3D game is clicked and dragged constantly. The arrival is the signal, not every
// sweep of the camera, so the store never sees more than a handful of clicks a visit.
const MAX_CLICKS = 12;
const CLICK_GAP_MS = 800;

function bounded(value, max) {
  // Everything below space is a control character, so a negated range from space up
  // drops them all; the angle brackets go with them, so a label can never read as markup.
  return String(value == null ? '' : value)
    .replace(/[<>]|[^\u0020-\u007e\u00a0-\uffff]/g, '')
    .trim()
    .slice(0, max);
}

// The exact link matters (?utm_), anything credential-shaped does not.
const SENSITIVE_PARAM = /token|secret|passw|key|sig|auth|session|otp|code|email/i;
function safeQuery(search) {
  let kept;
  try {
    kept = new window.URLSearchParams();
    new window.URLSearchParams(search || '').forEach(function (value, name) {
      if (!SENSITIVE_PARAM.test(name)) kept.append(name, value);
    });
  } catch {
    return '';
  }
  const out = kept.toString();
  return out ? '?' + out : '';
}

function visitorId() {
  const minted = 'v_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  try {
    const stored = window.localStorage.getItem(VISITOR_KEY);
    if (stored) return stored;
    window.localStorage.setItem(VISITOR_KEY, minted);
  } catch {
    // Private mode: the visit still gets one id, it just does not outlive the page.
  }
  return minted;
}

function touchFromLocation() {
  let referrer = '';
  try {
    if (document.referrer) {
      const ref = new window.URL(document.referrer);
      if (ref.origin !== window.location.origin) {
        referrer = ref.origin + bounded(ref.pathname + safeQuery(ref.search), 220);
      }
    }
  } catch {
    // A referrer we cannot parse is no referrer.
  }
  let source = '';
  try {
    source = new window.URLSearchParams(window.location.search).get('utm_source') || '';
  } catch {
    source = '';
  }
  // The reader resolves the assistant from source by exact host match, which is what
  // ChatGPT's own link decoration leaves (utm_source=chatgpt.com), and from the
  // referrer host when the source is only "referral", which is how Perplexity lands.
  return {
    source: bounded(source || (referrer ? 'referral' : 'direct'), 80),
    referrer: bounded(referrer, 300),
    landing: bounded(window.location.pathname + safeQuery(window.location.search), 280)
  };
}

/* One visit keeps the arrival it came in with, so every row of it names the same
   assistant and the reader can group them into one session. */
function visitTouch() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(TOUCH_KEY) || 'null');
    if (stored && stored.source) return stored;
  } catch {
    // No storage, or a value we did not write: fall through to a fresh reading.
  }
  const fresh = touchFromLocation();
  try {
    window.sessionStorage.setItem(TOUCH_KEY, JSON.stringify(fresh));
  } catch {
    // Nothing to keep it in: this page still reports the arrival correctly.
  }
  return fresh;
}

/* Our own checks must never reach the project's ledger: ?bles_internal=1 marks the
   visit, and it stays marked for the rest of it. */
function internalVisit() {
  let flagged = false;
  try {
    flagged = new window.URLSearchParams(window.location.search).get('bles_internal') === '1';
  } catch {
    flagged = false;
  }
  try {
    if (flagged) window.sessionStorage.setItem(INTERNAL_KEY, '1');
    return flagged || window.sessionStorage.getItem(INTERNAL_KEY) === '1';
  } catch {
    return flagged;
  }
}

function post(payload) {
  const body = JSON.stringify(payload);
  // text/plain keeps this a simple cross-origin request: no preflight, which is what
  // lets the pagehide row leave at all.
  try {
    const blob = new window.Blob([body], { type: 'text/plain;charset=UTF-8' });
    if (window.navigator.sendBeacon && window.navigator.sendBeacon(ENDPOINT, blob)) return;
  } catch {
    // sendBeacon refused the payload: the fetch below is the fallback.
  }
  try {
    window.fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: body,
      keepalive: true
    }).catch(function () {});
  } catch {
    // Measurement is never allowed to be the reason a visitor sees a broken game.
  }
}

function start() {
  // Only on the host this measures: a dev server, a preview or an end-to-end run is
  // not a visitor, and must never write into the ledger.
  if (window.location.hostname !== HOST) return;
  // The beacon reports a visit, not a frame of one: it is silent in any iframe.
  try {
    if (window.top !== window.self) return;
  } catch {
    return;
  }

  const visitor = visitorId();
  const touch = visitTouch();
  const internal = internalVisit();

  function send(type, label, final) {
    post({
      app: APP,
      type: type,
      visitor: visitor,
      page: bounded(window.location.pathname, 160),
      label: bounded(label, 80),
      source: touch.source,
      referrer: touch.referrer,
      landing: touch.landing,
      internal: internal,
      final: final === true
    });
  }

  send('page_view', document.title, false);

  let clicks = 0;
  let lastClickAt = 0;
  document.addEventListener('click', function (clickEvent) {
    const now = Date.now();
    if (clicks >= MAX_CLICKS || now - lastClickAt < CLICK_GAP_MS) return;
    clicks += 1;
    lastClickAt = now;
    const target = clickEvent.target;
    const element = target && target.closest ? target.closest('a,button,[data-track]') : null;
    // Coarse on purpose: what was pressed, never who pressed it.
    const label = element
      ? (element.getAttribute('data-track') || element.getAttribute('aria-label') ||
         element.textContent || element.id || element.tagName).replace(/\s+/g, ' ')
      : (target && target.tagName ? target.tagName.toLowerCase() : 'page');
    send('click', label, false);
  }, true);

  // The last row of the visit. The reader's dwell time is the last row minus the
  // first, so without this one every visit would read as 0 seconds.
  let closed = false;
  window.addEventListener('pagehide', function () {
    if (closed) return;
    closed = true;
    send('page_view', 'pagehide', true);
  });
}

start();
